/** Pure helpers for the broker Compliance / KYC tab (#331). No React. */

export const PAN_RE = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
export const IFSC_RE = /^[A-Z]{4}0[A-Z0-9]{6}$/;
export const FY_RE = /^(\d{4})-(\d{2})$/;

export const LICENCE_CATEGORIES = [
  { code: 'life', label: 'Life' },
  { code: 'general', label: 'General' },
  { code: 'health', label: 'Health' },
  { code: 'reinsurance', label: 'Reinsurance' },
  { code: 'composite', label: 'Composite' },
] as const;

export const REQUIRED_DOC_TYPES = ['pan', 'gst', 'cin', 'irdai_licence'] as const;

/** Field-level message, or null when the value is acceptable. */
export function validateBankAccount(input: { ifsc: string; account_number: string }): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!IFSC_RE.test(input.ifsc.trim().toUpperCase())) errors.ifsc = 'Enter a valid IFSC (for example HDFC0001234).';
  if (!/^\d{6,20}$/.test(input.account_number.replace(/\s+/g, ''))) {
    errors.account_number = 'An account number is 6 to 20 digits.';
  }
  return errors;
}

export function validateFinancialYear(fy: string): string | null {
  const match = FY_RE.exec(fy.trim());
  if (!match || (Number(match[1]) + 1) % 100 !== Number(match[2])) return 'Use a financial year such as 2025-26.';
  return null;
}

export function validatePan(pan: string): string | null {
  return PAN_RE.test(pan.trim().toUpperCase()) ? null : 'Enter a valid PAN (for example ABCDE1234F).';
}

export type KycStatusTone = 'neutral' | 'info' | 'success' | 'warning';

export function kycStatusLabel(status: string): { label: string; tone: KycStatusTone } {
  switch (status) {
    case 'submitted':
      return { label: 'Submitted - awaiting review', tone: 'info' };
    case 'verified':
      return { label: 'Verified', tone: 'success' };
    case 'changes_requested':
      return { label: 'Changes requested', tone: 'warning' };
    default:
      return { label: 'Incomplete', tone: 'neutral' };
  }
}

/** The broker admin can submit from these states only, and only when nothing is missing. */
export function canSubmit(status: string, missingCount: number): boolean {
  return (status === 'incomplete' || status === 'changes_requested') && missingCount === 0;
}

/** Only the broker admin edits; a country admin or anyone else sees nothing here. */
export function canEditKyc(role: string | undefined): boolean {
  return role === 'broker_admin';
}

/** The tab exists only on the broker admin's own settings page. Other roles
 *  (a broker employee, or anyone viewing another broker) get 403/400 from the
 *  API, so they never see the tab rather than an error message. */
export function canSeeKycTab(role: string | undefined, viewingOtherBroker: boolean): boolean {
  return role === 'broker_admin' && !viewingOtherBroker;
}
